'use client'

import { useState, useMemo, useEffect, useRef } from 'react'
import {
  format,
  isSameMonth,
  isToday,
  parseISO,
} from 'date-fns'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useDebouncedCallback } from 'use-debounce'
import { useArLeadershipDates } from '@/features/volunteers/hooks/volunteers.hooks'
import { useStmGroupsForTab } from '@/features/stm/hooks/use-stm-weeks'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { ChevronLeft, ChevronRight, UsersRound, CalendarDays, Users, Shield, Search, Filter, X } from 'lucide-react'
import { buildRegionUrl, cn } from '@/shared/lib/utils'
import { useRegion } from '@/shared/hooks/use-region'
import type { NonHomeownerUser, VolunteerTableRow } from '@/features/volunteers/types/volunteers.type'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import UpdateUserForm from '@/features/volunteers/forms/user-form/update-user-form'
import { getNonHomeownerUserById } from '@/features/volunteers/actions/volunteers.actions'
import { MonthSpanCalendar } from '@/features/volunteers/components/month-span-calendar/MonthSpanCalendar'
import {
  buildCalendarWeeks,
  getWeekSpanSegments,
  compareArLeadershipSegments,
} from '@/features/volunteers/components/month-span-calendar/month-span-calendar-utils'
import { getTripParticipantCountForDay } from '@/features/volunteers/components/month-span-calendar/group-trip-calendar-utils'
import {
  SEARCH_PARAMS_TAB_KEY,
  VOLUNTEER_TAB_FILTER_SEARCH,
} from '@/features/volunteers/constants/volunteer-tab-links'

/** AR Leadership event: person + date range */
type ArLeadershipEvent = {
  userId: number
  fullname: string
  roleName: string
  from: Date
  to: Date
}

/** Transparent teal blue styling for all AR Team calendar event bars (slightly blue tint) */
const EVENT_BAR_CLASS = 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 border-cyan-500/40'

function parseServeDate(val: string | Date | undefined): Date | null {
  if (!val) return null
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val
  if (typeof val === 'string') {
    const d = /^\d{4}-\d{2}-\d{2}/.test(val) ? parseISO(val.split('T')[0]) : parseISO(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

function extractEvents(
  users: (NonHomeownerUser | VolunteerTableRow)[],
  regionId: number | undefined | null
): ArLeadershipEvent[] {
  const events: ArLeadershipEvent[] = []
  for (const user of users) {
    const serveDates = user.volunteer?.serveDates
    if (!serveDates || !Array.isArray(serveDates)) continue
    const fullname = user.fullname || 'Unknown'
    const roleName = (user.role as { name?: string })?.name ?? ''

    for (const period of serveDates) {
      if (regionId != null && regionId > 0) {
        const p = period as { from?: string; to?: string; regionId?: number }
        let periodRegionId: number | null = null
        if (p.regionId != null) {
          periodRegionId = typeof p.regionId === 'number' ? p.regionId : parseInt(String(p.regionId), 10)
          if (isNaN(periodRegionId)) periodRegionId = null
        }
        if (periodRegionId !== regionId) continue
      }
      const from = parseServeDate(period.from)
      const to = parseServeDate(period.to)
      if (!from || !to || from > to) continue
      events.push({ userId: user.id, fullname, roleName, from, to })
    }
  }
  return events
}

function arEventMatchesSearch(event: ArLeadershipEvent, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true
  return [
    event.fullname,
    event.roleName,
    format(event.from, 'MMM d, yyyy'),
    format(event.to, 'MMM d, yyyy'),
    format(event.from, 'M/d/yyyy'),
    format(event.to, 'M/d/yyyy'),
  ].some((value) => value.toLowerCase().includes(normalizedQuery))
}

function isArEventInMonth(event: ArLeadershipEvent, month: Date): boolean {
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1)
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0)
  return event.from <= monthEnd && event.to >= monthStart
}

interface ArTeamCalendarTabProps {
  regionId: number | undefined | null
}

export function ArTeamCalendarTab({ regionId }: ArTeamCalendarTabProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { regionSlug } = useRegion()
  const { data, isLoading, error } = useArLeadershipDates(regionId, { allRegions: false })
  const { data: groups } = useStmGroupsForTab(regionId)
  const [currentMonth, setCurrentMonth] = useState(() => new Date())
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [pendingScrollToToday, setPendingScrollToToday] = useState(false)
  const todayWeekRowRef = useRef<HTMLDivElement | null>(null)
  const [updateUserDialogOpen, setUpdateUserDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<NonHomeownerUser | VolunteerTableRow | null>(null)
  const [dialogUser, setDialogUser] = useState<NonHomeownerUser | null>(null)
  const [dialogUserLoading, setDialogUserLoading] = useState(false)
  const [dialogUserError, setDialogUserError] = useState<string | null>(null)

  useEffect(() => {
    setSearchQuery(searchParams.get('search') || '')
  }, [searchParams])

  const updateSearchParam = useDebouncedCallback((value: string) => {
    const params = new URLSearchParams(searchParams.toString())
    if (value && value.trim().length > 0) {
      params.set('search', value)
    } else {
      params.delete('search')
    }
    params.set('page', '1')
    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false })
  }, 750)

  const handleOpenVolunteerSearch = () => {
    const search = searchQuery.trim()
    if (!search) return
    updateSearchParam.cancel()
    const params = {
      [SEARCH_PARAMS_TAB_KEY]: VOLUNTEER_TAB_FILTER_SEARCH,
      page: '1',
      search,
    }
    const href = regionSlug
      ? buildRegionUrl(regionSlug, '/volunteers', params)
      : `/volunteers?${new URLSearchParams(params).toString()}`
    router.push(href)
  }

  const clearSearchParam = () => {
    updateSearchParam.cancel()
    setSearchQuery('')
    const params = new URLSearchParams(searchParams.toString())
    params.delete('search')
    params.set('page', '1')
    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false })
  }

  useEffect(() => {
    if (!updateUserDialogOpen || !selectedUser?.id) {
      setDialogUser(null)
      setDialogUserError(null)
      return
    }
    setDialogUserLoading(true)
    setDialogUserError(null)
    setDialogUser(null)
    getNonHomeownerUserById(selectedUser.id)
      .then((res) => {
        if (res.success && res.data) setDialogUser(res.data)
        else setDialogUserError(res.success ? 'Failed to load user' : (res.message ?? 'Failed to load user'))
      })
      .catch(() => setDialogUserError('Failed to load user'))
      .finally(() => setDialogUserLoading(false))
  }, [updateUserDialogOpen, selectedUser?.id])

  const users = useMemo(
    () => (data?.success && data?.data?.users ? data.data.users : []),
    [data]
  )
  const events = useMemo(() => extractEvents(users, regionId), [users, regionId])
  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  const filteredEvents = useMemo(
    () => events.filter((event) => arEventMatchesSearch(event, normalizedSearchQuery)),
    [events, normalizedSearchQuery]
  )
  const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users])

  const weeks = useMemo(() => buildCalendarWeeks(currentMonth), [currentMonth])

  useEffect(() => {
    if (!normalizedSearchQuery || filteredEvents.length === 0) return
    const currentMonthHasMatch = filteredEvents.some((event) => isArEventInMonth(event, currentMonth))
    if (currentMonthHasMatch) return

    const firstMatchedEvent = [...filteredEvents].sort((a, b) => a.from.getTime() - b.from.getTime())[0]
    if (!firstMatchedEvent) return

    const matchedMonth = new Date(firstMatchedEvent.from.getFullYear(), firstMatchedEvent.from.getMonth(), 1)
    const currentMonthKey = format(currentMonth, 'yyyy-MM')
    const matchedMonthKey = format(matchedMonth, 'yyyy-MM')
    if (matchedMonthKey !== currentMonthKey) {
      setCurrentMonth(matchedMonth)
    }
  }, [currentMonth, filteredEvents, normalizedSearchQuery])

  const handlePrevMonth = () => {
    setCurrentMonth((p) => new Date(p.getFullYear(), p.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setCurrentMonth((p) => new Date(p.getFullYear(), p.getMonth() + 1, 1))
  }

  const handleToday = () => {
    setCurrentMonth(new Date())
    setPendingScrollToToday(true)
  }

  useEffect(() => {
    if (!pendingScrollToToday) return
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        todayWeekRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        setPendingScrollToToday(false)
      })
    })
    return () => cancelAnimationFrame(id)
  }, [pendingScrollToToday, currentMonth])

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-destructive font-medium">Failed to load AR Team calendar</p>
        <p className="text-sm text-muted-foreground mt-1">Please try again later.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <UsersRound className="h-5 w-5 text-primary" />
            AR Team
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Base Leaders Dates On Base
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1 bg-muted/50 rounded-xl p-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handlePrevMonth}
              className="rounded-lg h-9 w-9"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h3 className="text-base sm:text-lg font-bold min-w-[140px] sm:min-w-[160px] text-center px-2">
              {format(currentMonth, 'MMMM yyyy')}
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleNextMonth}
              className="rounded-lg h-9 w-9"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleToday}
            className="rounded-lg font-medium"
          >
            Today
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className="relative flex-1 min-w-[220px] sm:min-w-[360px] lg:min-w-[520px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground pointer-events-none z-10" />
          <Input
            placeholder="Search volunteers, groups, dates..."
            value={searchQuery}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value
              setSearchQuery(value)
              updateSearchParam(value)
            }}
            className="pl-8 sm:pl-9 h-8 sm:h-10 text-sm"
          />
        </div>
        {searchQuery.trim().length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleOpenVolunteerSearch}
            className="h-8 sm:h-10 shrink-0 text-xs sm:text-sm whitespace-nowrap"
          >
            Search volunteers
          </Button>
        )}
      </div>

      {searchQuery.trim().length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm text-muted-foreground">
            <span className="font-bold">
              Showing {filteredEvents.length.toLocaleString()} result{filteredEvents.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="w-4 h-4" />
              <span>Active filters:</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm">
              <span className="font-medium">Search:</span>
              <span>{searchQuery.trim()}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-0 ml-1 hover:bg-primary/20"
                onClick={clearSearchParam}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearSearchParam}
              className="text-xs"
            >
              Clear all
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-[400px] w-full rounded-xl" />
        </div>
      ) : (
        <>
          <MonthSpanCalendar
            className="rounded-xl border bg-card text-card-foreground overflow-hidden"
            showMonthToolbar={false}
            currentMonth={currentMonth}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onToday={handleToday}
            weeks={weeks}
            todayWeekRowRef={todayWeekRowRef}
            isLoading={false}
            getSegmentsForWeek={(weekStart) =>
              getWeekSpanSegments(filteredEvents, weekStart, compareArLeadershipSegments)
            }
            renderDayCell={({ day, dayCol, weekSegments, isCurrentMonth, isTodayDate }) => {
              const participantCount = getTripParticipantCountForDay(groups ?? [], day).participantSum
              const baseLeaderCount = weekSegments.filter(
                (seg) => seg.startCol <= dayCol && seg.endCol >= dayCol
              ).length
              const hasCount = participantCount > 0 || baseLeaderCount > 0
              return (
                <div
                  className={cn(
                    'h-full border-r border-muted-foreground/40 last:border-r-0 p-2 sm:p-1.5 flex flex-col sm:flex-row sm:items-center justify-between gap-1 min-h-[52px] sm:min-h-[36px]',
                    !isCurrentMonth && 'bg-muted/20',
                    isTodayDate && 'bg-primary/5'
                  )}
                >
                  <span
                    className={cn(
                      'text-sm font-medium inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-full shrink-0',
                      isCurrentMonth ? 'text-foreground' : 'text-muted-foreground',
                      isTodayDate && 'bg-primary text-primary-foreground'
                    )}
                  >
                    {format(day, 'd')}
                  </span>
                  {hasCount && (
                    <span className="flex flex-col sm:flex-row items-end sm:items-center gap-0.5 sm:gap-1.5 text-sm font-semibold text-muted-foreground shrink-0 sm:ml-auto">
                      <span className="flex items-center gap-1" title="Participants">
                        <Users className="h-4 w-4 sm:h-3.5 sm:w-3.5 shrink-0" />
                        <span>{participantCount}</span>
                      </span>
                      <span className="flex items-center gap-1" title="Base Leaders">
                        <Shield className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-teal-600 dark:text-teal-400 shrink-0" />
                        <span>{baseLeaderCount}</span>
                      </span>
                    </span>
                  )}
                </div>
              )
            }}
            renderWeekBarRow={(weekStart, _weekIdx, weekSegments) => {
              return (
                <>
                  {weekSegments.map((seg) => {
                    const colStart = seg.startCol + 1
                    const colEnd = seg.endCol + 2
                    const user = usersById.get(seg.event.userId)
                    const handleClick = () => {
                      if (user) {
                        setSelectedUser(user)
                        setUpdateUserDialogOpen(true)
                      }
                    }
                    return (
                      <button
                        type="button"
                        key={`${seg.event.userId}-${seg.event.from.toISOString()}-${weekStart.toISOString()}`}
                        className={cn(
                          'col-span-1 self-start rounded-md px-2 py-1 text-xs font-medium border truncate text-left cursor-pointer hover:opacity-90 transition-opacity',
                          EVENT_BAR_CLASS
                        )}
                        style={{
                          gridColumn: `${colStart} / ${colEnd}`,
                        }}
                        title={`${seg.event.fullname} ${format(seg.event.from, 'MMM d')} – ${format(seg.event.to, 'MMM d')}`}
                        onClick={handleClick}
                      >
                        {seg.event.fullname}
                      </button>
                    )
                  })}
                </>
              )
            }}
          />

          {filteredEvents.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground border rounded-xl border-border bg-card">
              <CalendarDays className="h-10 w-10 opacity-40 mb-2" />
              <p className="text-sm font-medium">
                {normalizedSearchQuery
                  ? 'No Base Leaders match this search'
                  : 'No Base Leaders scheduled for this region yet'}
              </p>
              <p className="text-xs mt-0.5">
                Base Leaders and Admins can add Dates Served with region in their Profile.
              </p>
            </div>
          )}
        </>
      )}

      <Dialog
        open={updateUserDialogOpen}
        onOpenChange={(open) => {
          setUpdateUserDialogOpen(open)
          if (!open) {
            setSelectedUser(null)
            setDialogUser(null)
            setDialogUserError(null)
          }
        }}
      >
        <DialogContent className="pt-10 pb-7 px-5 max-w-[600px] md:max-w-[900px] lg:max-w-6xl max-h-[80vh] w-[90%] overflow-y-auto grid-rows-[auto_1fr] md:px-8 md:pt-10 md:pb-8" aria-describedby={undefined}>
          <div className="flex items-center flex-col gap-2 text-center w-full">
            <DialogTitle className="text-2xl font-bold text-center w-full">Update Volunteer</DialogTitle>
            <DialogDescription className="text-center w-full">Update Volunteer information.</DialogDescription>
          </div>
          {dialogUserLoading && <p className="text-muted-foreground text-sm">Loading…</p>}
          {dialogUserError && <p className="text-destructive text-sm">{dialogUserError}</p>}
          {dialogUser && !dialogUserLoading && (
            <UpdateUserForm
              user={dialogUser}
              onSuccessCallback={() => {
                setUpdateUserDialogOpen(false)
                setSelectedUser(null)
                setDialogUser(null)
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
